import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import LoadingWheel from "../../shared/LoadingWheel";
import { ButtonInput } from "../../shared/Inputs/ButtonInput";
import Modal from "../../../layouts/Modal";
import { TextInput } from "../../shared/Inputs/TextInput";
import useSettingSet, { settingSetQueryKey } from "../../../data/useSettingSet";
import useRsAutomotive from "../../../data/useRsAutomotive";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";


type RsRegisterPayload = {
  user_name: string;
  email: string;
  company_name: string;
  country: string; // e.g. "gb"
  phone: string;
};

type RsRegisterResponse = {
  rs_status?: string;
  rs_message?: string;
  rs_data?: {
    rs_user_name?: string;
    rs_email?: string;
    rs_plain_password?: string;
    rs_api_key?: string;
    rs_message?: string;
  };
  rs_timestamp?: string;
};

export default function RsCoreConnection() {
  const queryClient = useQueryClient();
  const { isSet: isConnected, isLoading } = useSettingSet("Rs:ApiKey");
  const { registerUser  } = useRsAutomotive();
  const { saveHandlers  } = useDefaultCRUDHandlers("RS Automotive");

  const [open, setOpen] = useState(false);

  // After successful register, show credentials one-time
  const [creds, setCreds] = useState<{
    username?: string;
    password?: string;
  } | null>(null);

  // react-hook-form
  const { register, handleSubmit, reset } = useForm<RsRegisterPayload>({
    defaultValues: {
      user_name: "",
      email: "",
      company_name: "",
      country: "gb",
      phone: "",
    },
  });

  useEffect(() => {
    // When opening modal, start fresh
    if (open) {
      setCreds(null);
      reset({
        user_name: "",
        email: "",
        company_name: "",
        country: "gb",
        phone: "",
      });
    } else {
      // When closing, clear sensitive info
      setCreds(null);
      reset({
        user_name: "",
        email: "",
        company_name: "",
        country: "gb",
        phone: "",
      });
    }
  }, [open, reset]);

  const onSubmit = (data: RsRegisterPayload) => {
    registerUser.mutate(data, {
      ...saveHandlers,
      onSuccess: (res: any) => {
        saveHandlers.onSuccess();

        const typed = res as RsRegisterResponse;
        const username = typed?.rs_data?.rs_user_name;
        const password = typed?.rs_data?.rs_plain_password;

        setCreds({ username, password });

        queryClient.invalidateQueries(settingSetQueryKey("Rs:ApiKey"));
      },
      onError: (err: any) => {
        saveHandlers.onError();
      },
    });
  };

  return (
    <>
      <div className="py-6 px-4 flex items-center justify-between">
        <p>RS Automotive</p>

        {isLoading ? (
          <LoadingWheel />
        ) : isConnected ? (
          <div>
            <span className="text-green-700 font-medium text-sm">Connected</span>
          </div>
        ) : (
          <>
            <span className="text-gray-400 italic text-sm mr-4">Not connected</span>
            <ButtonInput
              label="Connect to RS Automotive"
              isSubmit={false}
              onClick={() => setOpen(true)}
              classes={undefined}
            />
          </>
        )}
      </div>

      <Modal width="max-w-2xl" open={open} setOpen={setOpen}>
        {!creds ? (
          <>
            <h2 className="text-xl font-semibold mb-2">Connect RS Automotive</h2>
            <p className="text-sm text-gray-600 mb-6">
              Register your RS Automotive user. Country should be a 2-letter code (e.g. <span className="font-mono">gb</span>).
            </p>

            <form
              onSubmit={handleSubmit(onSubmit)}
              className="grid grid-cols-1 sm:grid-cols-2 gap-4"
            >
              <TextInput
                label="User Name"
                register={register}
                registerName="user_name"
              />
              <TextInput
                label="Email"
                register={register}
                registerName="email"
              />
              <TextInput
                label="Company Name"
                register={register}
                registerName="company_name"
                cols="col-span-2"
              />
              <TextInput
                label="Country Code"
                register={register}
                registerName="country"
                placeholder="gb"
              />
              <TextInput
                label="Phone"
                register={register}
                registerName="phone"
              />

              <div className="sm:col-span-2 mt-4 flex items-center justify-end gap-2">
                <ButtonInput
                  label="Cancel"
                  isSubmit={false}
                  onClick={() => setOpen(false)}
                  classes=""
                />
                <ButtonInput
                  label={registerUser.isLoading ? "Registering..." : "Register"}
                  isSubmit={true}
                  onClick={undefined}
                  classes=""
                />
              </div>
            </form>
          </>
        ) : (
          <>
            <h2 className="text-xl font-semibold mb-2">RS Automotive Connected</h2>

            <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 mb-4">
              <p className="font-semibold text-yellow-900">
                Save these credentials now
              </p>
              <p className="text-sm text-yellow-900 mt-1">
                This is the only time we can show your RS username and password.
                Once you close this modal, they cannot be retrieved again.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="rounded-lg border p-3 bg-white">
                <p className="text-xs text-gray-500 mb-1">Username</p>
                <p className="font-mono text-sm break-all">{creds.username ?? "-"}</p>
              </div>

              <div className="rounded-lg border p-3 bg-white">
                <p className="text-xs text-gray-500 mb-1">Password</p>
                <p className="font-mono text-sm break-all">{creds.password ?? "-"}</p>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end">
              <ButtonInput
                label="Close"
                isSubmit={false}
                onClick={() => setOpen(false)}
                classes=""
              />
            </div>
          </>
        )}
      </Modal>
    </>
  );
}
